import { useState } from 'react'
import { BookOpen } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useLayoutPanel } from '@/workspace/use-layout-panel'
import type { NotebookPanelState, NotebookView } from '../schemas/note-types'
import { MODE_OPTIONS, VIEW_LABEL } from '../lib/view-labels'
import { textPx } from '@/features/theme-editor/text-scale'

/**
 * Header mode selector for the notebook panel. Lives in the left slot of the
 * panel header inside `.panel-header-pill`, so the trigger is transparent and
 * the pill supplies the box (the `ScanNameSelector` pattern). Mirrors the rail
 * tabs so navigation survives the rail collapsing to icons or hiding.
 */
export function NotebookModeSelect({ panelId }: { panelId: string }) {
  const { panelState, updatePanel } = useLayoutPanel<NotebookPanelState>(panelId)
  const view: NotebookView = panelState?.view ?? 'feed'
  const [open, setOpen] = useState(false)

  const select = (next: NotebookView) => {
    // Same shape as the context's setView: switching mode exits any open editor.
    updatePanel({ view: next, viewMode: { mode: 'stream' } })
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notebook-mode-select"
          onMouseDown={(e) => e.stopPropagation()}
          onDragStart={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          draggable={true}
          className="flex items-center justify-between rounded border-none bg-transparent text-muted-foreground transition-colors"
          style={{
            fontSize: textPx(13),
            fontWeight: 400,
            cursor: 'pointer',
            padding: '0 10px',
            gap: 6,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
            <BookOpen size={12} strokeWidth={1.8} style={{ flexShrink: 0, opacity: 0.7 }} />
            <span style={{ whiteSpace: 'nowrap' }}>{VIEW_LABEL[view]}</span>
          </span>
          <svg
            style={{ flexShrink: 0 }}
            width={10}
            height={10}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[170px] p-0"
        align="start"
        sideOffset={4}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {MODE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            role="option"
            aria-selected={view === opt.value}
            data-testid={`notebook-mode-${opt.value}`}
            onClick={() => select(opt.value)}
            className="flex w-full items-center px-3 py-1.5 text-left rounded-sm border-none bg-transparent cursor-pointer hover:bg-hover hover:text-accent-foreground transition-colors"
          >
            <span
              style={{
                fontSize: textPx(13),
                fontWeight: 400,
                color: view === opt.value ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
              }}
            >
              {opt.label}
            </span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}
